/**
 * Client-side text-fit pass for AI-generated slide content.
 *
 * The LLM can produce text elements whose box is too small for their text,
 * which renders as clipped text in [SlideElement.tsx](../components/SlideElement.tsx).
 * Rather than estimate overflow with a heuristic on the server, we measure it
 * here: mount each text element offscreen into a hidden sandbox, await font
 * loading, and check whether the text extends past the padding-box clip edge.
 * If it overflows, binary-search-shrink `fontSize` down to a hard floor.
 *
 * The fit pass runs on the streamed AI draft before it lands in chat state,
 * so the persisted draft is the fitted draft. Existing stored slides are not
 * touched.
 *
 * Determinism: same browser + same loaded fonts → same measurement. A small
 * safety margin absorbs subpixel variance across machines.
 */

import {
  DEFAULT_SLIDE_BODY_FONT_SIZE_PT,
  SLIDE_PAGE_HEIGHT,
  SLIDE_PAGE_WIDTH,
  type ResolvedTheme,
  type Slide,
  type SlideContentV1,
  type SlideElement,
  type SlideTextStyle,
} from '@/shared/types/slide';
import { DEFAULT_THEME } from '@/shared/theming/default-theme';
import { resolveStoredElement } from '@/shared/theming/resolve';
import { textStyleToCss } from '@/features/slides/components/SlideElement';

/** Hard floor — never shrink below this (in pt). Slides that still overflow get a warning. */
export const FONT_SIZE_FLOOR = 9;

/** Subpixel slack to absorb cross-platform rasterization differences. */
const OVERFLOW_SAFETY_MARGIN_PX = 2;

export interface TextFitWarning {
  changeId?: string;
  slideId: string;
  elementId: string;
  kind: 'still_overflows';
}

// ─── Offscreen sandbox ─────────────────────────────────────────────────────

let measureNode: HTMLDivElement | null = null;

/** Lazily creates the offscreen measurement node once per page session. */
function ensureMeasureNode(): HTMLDivElement | null {
  if (typeof document === 'undefined') return null;
  if (measureNode) return measureNode;

  const sandbox = document.createElement('div');
  sandbox.setAttribute('data-text-fit-sandbox', '');
  sandbox.style.position = 'absolute';
  sandbox.style.left = '-99999px';
  sandbox.style.top = '0';
  sandbox.style.width = `${SLIDE_PAGE_WIDTH}px`;
  sandbox.style.height = `${SLIDE_PAGE_HEIGHT}px`;
  sandbox.style.visibility = 'hidden';
  sandbox.style.pointerEvents = 'none';

  const node = document.createElement('div');
  sandbox.appendChild(node);
  document.body.appendChild(sandbox);
  measureNode = node;
  return measureNode;
}

// ─── CSS application ───────────────────────────────────────────────────────

// Properties from textStyleToCss whose numeric values are unitless when applied
// to inline DOM styles. Other numeric values (padding, borderWidth,
// borderRadius) take 'px'. fontSize is already serialized as a pt string by
// textStyleToCss so measurement matches the on-canvas renderer.
const UNITLESS_KEYS = new Set(['lineHeight', 'fontWeight', 'opacity', 'zIndex']);

/**
 * Applies the styles produced by textStyleToCss to a raw DOM node, converting
 * numeric values to px / unitless strings as React would. We import
 * textStyleToCss instead of duplicating its body so the measurement node is
 * styled identically to the on-canvas renderer.
 */
function applyTextCssToNode(
  node: HTMLDivElement,
  style: SlideTextStyle,
  width: number,
  height: number
): void {
  const css = textStyleToCss(style);
  node.removeAttribute('style');
  for (const [key, value] of Object.entries(css)) {
    if (value === undefined || value === null) continue;
    let serialized: string;
    if (typeof value === 'number') {
      serialized = UNITLESS_KEYS.has(key) ? String(value) : `${value}px`;
    } else {
      serialized = String(value);
    }
    (node.style as unknown as Record<string, string>)[key] = serialized;
  }
  // Override width/height — textStyleToCss returns 100% (sized by the parent on
  // canvas); for measurement we want the actual element box dimensions.
  node.style.width = `${width}px`;
  node.style.height = `${height}px`;
}

// ─── Measurement + shrink ──────────────────────────────────────────────────

function parseNonNegativePx(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function overflowsAt(
  node: HTMLDivElement,
  style: SlideTextStyle,
  width: number,
  height: number,
  fontSize: number,
  text: string
): boolean {
  applyTextCssToNode(node, { ...style, fontSize }, width, height);
  node.textContent = text;
  // The renderer clips text at the padding box, so content may use bottom/right
  // padding before it is actually cut off.
  const verticalAllowance =
    parseNonNegativePx(node.style.paddingBottom) + OVERFLOW_SAFETY_MARGIN_PX;
  const horizontalAllowance =
    parseNonNegativePx(node.style.paddingRight) + OVERFLOW_SAFETY_MARGIN_PX;

  return (
    node.scrollHeight - node.clientHeight > verticalAllowance ||
    node.scrollWidth - node.clientWidth > horizontalAllowance
  );
}

/**
 * Largest integer fontSize in [floor, current] that fits, via binary search.
 * Returns `floor` if even the floor overflows.
 */
function largestFittingFontSize(
  node: HTMLDivElement,
  style: SlideTextStyle,
  width: number,
  height: number,
  text: string,
  current: number,
  floor: number
): number {
  let lo = floor;
  let hi = Math.floor(current);
  let best = floor;
  // Quick check: if the floor doesn't fit, the answer is floor (capped below).
  if (overflowsAt(node, style, width, height, lo, text)) return floor;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (overflowsAt(node, style, width, height, mid, text)) {
      hi = mid - 1;
    } else {
      best = mid;
      lo = mid + 1;
    }
  }
  return best;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Resolves a deck theme out of a loosely-typed draft presentation (or falls back
 *  to DEFAULT_THEME). The stored shape is trusted; Phase 1 has a single theme. */
function themeOf(presentation: Record<string, unknown>): ResolvedTheme {
  return isRecord(presentation.theme)
    ? (presentation.theme as unknown as ResolvedTheme)
    : DEFAULT_THEME;
}

let fontsReadyPromise: Promise<void> | null = null;
function awaitFontsReady(): Promise<void> {
  if (fontsReadyPromise) return fontsReadyPromise;
  if (typeof document === 'undefined' || !document.fonts?.ready) {
    fontsReadyPromise = Promise.resolve();
  } else {
    fontsReadyPromise = document.fonts.ready.then(() => undefined);
  }
  return fontsReadyPromise;
}

/**
 * Fits the text elements of one slide. Each text element is resolved against the
 * deck theme first (§6.3d) so a placeholder-bound element is measured with the
 * layout's real geometry + font/size, not the (possibly absent) stored values.
 * When it shrinks, the new `fontSize` is written back as a per-field override on
 * the STORED element (§7.6) — other style fields keep inheriting.
 *
 * Works for both stored slides (elements carry ids) and authoring patch-op slides
 * (no ids): resolution is per-element, never id-matched. `slideId` only tags warnings.
 */
async function fitSlide(
  rawSlide: Record<string, unknown>,
  theme: ResolvedTheme,
  slideId: string
): Promise<{ elements: unknown[]; warnings: TextFitWarning[] }> {
  const elements = Array.isArray(rawSlide.elements) ? rawSlide.elements : [];
  const node = ensureMeasureNode();
  if (!node) return { elements, warnings: [] };
  await awaitFontsReady();

  // The draft slide is structurally a Slide; the resolver reads only
  // template/elements/per-element fields, all present on draft content.
  const slideForResolve = rawSlide as unknown as Slide;

  const warnings: TextFitWarning[] = [];
  const out = elements.map((element) => {
    if (!isRecord(element) || element.type !== 'text') return element;
    const resolved = resolveStoredElement(
      element as unknown as SlideElement,
      slideForResolve,
      theme
    );
    if (resolved.type !== 'text') return element;

    const { width, height } = resolved.position;
    const style = resolved.style;
    const text =
      isRecord(element.content) && typeof element.content.text === 'string'
        ? element.content.text
        : '';
    const elementId = typeof element.id === 'string' ? element.id : '';
    const currentFontSize =
      typeof style.fontSize === 'number' ? style.fontSize : DEFAULT_SLIDE_BODY_FONT_SIZE_PT;

    if (currentFontSize <= FONT_SIZE_FLOOR) {
      if (overflowsAt(node, style, width, height, currentFontSize, text)) {
        warnings.push({ slideId, elementId, kind: 'still_overflows' });
      }
      return element;
    }
    if (!overflowsAt(node, style, width, height, currentFontSize, text)) {
      return element;
    }
    const fitted = largestFittingFontSize(
      node,
      style,
      width,
      height,
      text,
      currentFontSize,
      FONT_SIZE_FLOOR
    );
    if (fitted === FONT_SIZE_FLOOR && overflowsAt(node, style, width, height, fitted, text)) {
      warnings.push({ slideId, elementId, kind: 'still_overflows' });
    }
    const storedStyle = isRecord(element.style) ? element.style : {};
    return { ...element, style: { ...storedStyle, fontSize: fitted } };
  });

  return { elements: out, warnings };
}

// ─── Public API ────────────────────────────────────────────────────────────

/**
 * Fits text in a `SlideContentV1` envelope. Returns a new content object (does
 * not mutate the input) and the list of warnings.
 *
 * Loose typing on the input: drafts arrive from the server as
 * `Record<string, unknown>`, so we narrow defensively here.
 */
export async function fitSlideContent(
  content: unknown
): Promise<{ content: unknown; warnings: TextFitWarning[] }> {
  if (typeof content !== 'object' || content === null) return { content, warnings: [] };
  const root = content as Record<string, unknown>;
  const presentation = root.presentation;
  if (typeof presentation !== 'object' || presentation === null) return { content, warnings: [] };
  const pres = presentation as Record<string, unknown>;
  const slides = pres.slides;
  if (!Array.isArray(slides)) return { content, warnings: [] };

  const theme = themeOf(pres);
  const allWarnings: TextFitWarning[] = [];
  const nextSlides = [];
  for (const slide of slides) {
    if (typeof slide !== 'object' || slide === null) {
      nextSlides.push(slide);
      continue;
    }
    const s = slide as Record<string, unknown>;
    const slideId = typeof s.id === 'string' ? s.id : '';
    const fitted = await fitSlide(s, theme, slideId);
    allWarnings.push(...fitted.warnings);
    nextSlides.push({ ...s, elements: fitted.elements });
  }

  const fittedContent = {
    ...root,
    presentation: { ...pres, slides: nextSlides },
  } as SlideContentV1;
  return { content: fittedContent, warnings: allWarnings };
}

/**
 * Fits a single authoring slide (as used inside `slides.patch_ops` `insert_slide`
 * / `replace_slide` ops). The slide has no embedded theme, so the caller passes
 * the deck theme (DEFAULT_THEME in Phase 1 — §7.6). Placeholder-bound elements are
 * resolved against it before measuring.
 */
export async function fitSlideInput(
  slide: unknown,
  theme: ResolvedTheme = DEFAULT_THEME
): Promise<{ slide: unknown; warnings: TextFitWarning[] }> {
  if (typeof slide !== 'object' || slide === null) return { slide, warnings: [] };
  const s = slide as Record<string, unknown>;
  // Patch-op slides have no stable id at this layer — warnings get an empty
  // slideId; the caller (fitDraftSlides) tags with the changeId for context.
  const fitted = await fitSlide(s, theme, '');
  return {
    slide: { ...s, elements: fitted.elements },
    warnings: fitted.warnings,
  };
}
